import React, { useState, useEffect } from 'react';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { EmployeeManager, UserProfileManager } from '../utils/enhancedFirebaseUtils';
import EmployeeInviteModal from './EmployeeInviteModal';
import { useNotification } from '../contexts/NotificationContext';
import { auth } from '../firebase.config';
import { 
  Users, 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Upload,
  Download,
  FileText,
  Phone,
  IndianRupee,
  Building,
  UserCheck,
  Camera,
  FileImage,
  CreditCard,
  Briefcase,
  Wallet
} from 'lucide-react';
import EmployeeCashFlow from './EmployeeCashFlow';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import { calculateAnnualCTC, buildSalaryPayload, employeeNeedsSalaryMigration } from '../utils/salaryUtils';
import { migrateCompanyEmployeesSalary } from '../utils/employeeSalaryMigration';

// Employee roles
const EMPLOYEE_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full access to all features' },
  { value: 'manager', label: 'Manager', description: 'Department/team management' },
  { value: 'staff', label: 'Staff', description: 'Assigned operations only' },
  { value: 'employee', label: 'Employee', description: 'Basic access level' }
];

const EmployeeManagement = () => {
  const { 
    currentCompany, 
    employees, 
    loading, 
    error, 
    createEmployee, 
    updateEmployee,
    deleteEmployee,
    hasPermission,
    canAccessPage,
    loadCompanyEmployees,
    loadUserCompanies
  } = useEnhancedCompany();
  const { success, error: notifyError, warning } = useNotification();

  // UI States
  const [activeTab, setActiveTab] = useState('list'); // list, add, edit, cash
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployeeForCashFlow, setSelectedEmployeeForCashFlow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** When false (default), list hides inactive and soft-deleted employees */
  const [showInactiveDeleted, setShowInactiveDeleted] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [migratingSalary, setMigratingSalary] = useState(false);
  
  // Form Data - Migrated from Expenses.js
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    email: '',
    phone: '',
    designation: '',
    joiningDate: '',
    
    // KYC Information (MIGRATED FROM EXPENSES)
    personType: 'Employee',
    contact: '',
    aadhaar: '',
    pan: '',
    bankName: '',
    bankAccount: '',
    ifsc: '',
    address: '',
    
    // Salary (annual amounts)
    basicSalary: '',
    bonus: '',
    
    // Role & Status
    role: 'employee',
    status: 'active'
  });

  // Document Management (MIGRATED FROM EXPENSES)
  const [documents, setDocuments] = useState({
    aadhaarCard: null,
    panCard: null,
    bankPassbook: null,
    photo: null,
    resume: null,
    otherDocuments: []
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedEmployeeForInvite, setSelectedEmployeeForInvite] = useState(null);
  
  // Employee Addition States (UID optional, manual default)
  const [additionMethod, setAdditionMethod] = useState('manual');
  const [uidInput, setUidInput] = useState('');
  const [uidValidation, setUidValidation] = useState({ valid: null, error: '' });
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [employmentStatus, setEmploymentStatus] = useState(null);

  // KYC validation states (MIGRATED FROM EXPENSES)
  const [kycErrors, setKycErrors] = useState({
    contact: '',
    aadhaar: '',
    pan: '',
    bankName: '',
    bankAccount: '',
    ifsc: ''
  });

  // Load employees when component mounts or company changes
  useEffect(() => {
    const loadEmployees = async () => {
      if (!currentCompany?.companyId) {
        console.log('No company selected for employee management');
        return;
      }

      try {
        console.log('Loading employees for company:', currentCompany.companyId);
        const result = await EmployeeManager.getCompanyEmployees(currentCompany.companyId);
        console.log('Employee load result:', result);
        
        if (result.success) {
          console.log('Employees loaded:', result.data);
        } else {
          console.error('Failed to load employees:', result.error);
        }
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    };

    // Check access first
    if (!canAccessPage('employee_management')) {
      console.log('Access denied to employee management');
      return;
    }

    // Load employees
    loadEmployees();
  }, [currentCompany?.companyId, canAccessPage]);

  // KYC Validation Functions (MIGRATED FROM EXPENSES)
  const validateContact = (contact) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(contact)) {
      return 'Please enter a valid 10-digit mobile number starting with 6-9';
    }
    return '';
  };

  const validateAadhaar = (aadhaar) => {
    const aadhaarRegex = /^\d{12}$/;
    if (!aadhaarRegex.test(aadhaar)) {
      return 'Please enter a valid 12-digit Aadhaar number';
    }
    return '';
  };

  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(pan.toUpperCase())) {
      return 'Please enter a valid PAN number (e.g., ABCDE1234F)';
    }
    return '';
  };

  const validateIFSC = (ifsc) => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(ifsc.toUpperCase())) {
      return 'Please enter a valid IFSC code (e.g., SBIN0001234)';
    }
    return '';
  };

  const validateBankAccount = (account) => {
    if (account.length < 9 || account.length > 18) {
      return 'Bank account number should be between 9-18 digits';
    }
    return '';
  };

  const calculateCTC = () => calculateAnnualCTC(formData.basicSalary, formData.bonus);

  // Handle advanced form changes (MIGRATED FROM EXPENSES)
  const handleAdvancedFormChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time KYC validation
    if (name === 'contact') {
      setKycErrors(prev => ({...prev, contact: validateContact(value)}));
    } else if (name === 'aadhaar') {
      setKycErrors(prev => ({...prev, aadhaar: validateAadhaar(value)}));
    } else if (name === 'pan') {
      setKycErrors(prev => ({...prev, pan: validatePAN(value)}));
    } else if (name === 'ifsc') {
      setKycErrors(prev => ({...prev, ifsc: validateIFSC(value)}));
    } else if (name === 'bankAccount') {
      setKycErrors(prev => ({...prev, bankAccount: validateBankAccount(value)}));
    }
  };

  // Generate unique employee ID (MIGRATED FROM EXPENSES)
  const generateEmployeeId = () => {
    const employeeList = employees || [];
    const maxId = employeeList.reduce((max, emp) => {
      const match = (emp.employeeId || '').match(/^EMP(\d+)$/);
      return match ? Math.max(max, parseInt(match[1], 10)) : max;
    }, 0);
    return `EMP${String(maxId + 1).padStart(3, '0')}`;
  };

  // Handle form input changes - Use advanced handler for salary calculation
  const handleInputChange = (e) => {
    return handleAdvancedFormChange(e);
  };
  
  // UID-based Employee Addition Functions
  const handleUidInputChange = async (e) => {
    const uid = e.target.value.trim();
    setUidInput(uid);
    
    // Reset states
    setUidValidation({ valid: null, error: '' });
    setUserProfile(null);
    setEmploymentStatus(null);
    
    if (!uid) return;
    
    // Validate UID format
    const validation = UserProfileManager.validateUID(uid);
    if (!validation.valid) {
      setUidValidation({ valid: false, error: validation.error });
      return;
    }
    
    setUidValidation({ valid: true, error: '' });
    
    // Fetch user profile
    setProfileLoading(true);
    try {
      const [profileResult, employmentResult] = await Promise.all([
        UserProfileManager.getUserProfileByUID(uid),
        UserProfileManager.checkUserEmploymentStatus(uid, currentCompany?.companyId)
      ]);
      
      if (profileResult.success) {
        // Check for duplicate in current company first
        if (employmentResult.success && employmentResult.isDuplicateInCurrentCompany) {
          setUidValidation({ valid: false, error: employmentResult.error });
          setUserProfile(null);
          setEmploymentStatus(null);
          return;
        }
        
        setUserProfile(profileResult.profile);
        
        // Pre-fill form with user profile data
        setFormData(prev => ({
          ...prev,
          name: profileResult.profile.displayName || '',
          email: profileResult.profile.email || '',
          phone: profileResult.profile.phoneNumber || ''
        }));
      } else {
        setUidValidation({ valid: false, error: profileResult.error });
      }
      
      if (employmentResult.success) {
        setEmploymentStatus(employmentResult);
      }
      
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUidValidation({ valid: false, error: 'Failed to fetch user profile' });
    } finally {
      setProfileLoading(false);
    }
  };
  
  const handleAdditionMethodChange = (method) => {
    setAdditionMethod(method);
    
    // Reset UID-related states when switching to manual
    if (method === 'manual') {
      setUidInput('');
      setUidValidation({ valid: null, error: '' });
      setUserProfile(null);
      setEmploymentStatus(null);
    }
    
    // Reset form to clean state
    resetForm();
  };
  

  // Handle document upload
  const handleDocumentUpload = (docType, file) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      designation: '',
      joiningDate: '',
      personType: 'Employee',
      contact: '',
      aadhaar: '',
      pan: '',
      bankName: '',
      bankAccount: '',
      ifsc: '',
      address: '',
      basicSalary: '',
      bonus: '',
      role: 'employee',
      status: 'active'
    });
    setDocuments({
      aadhaarCard: null,
      panCard: null,
      bankPassbook: null,
      photo: null,
      resume: null,
      otherDocuments: []
    });
    setKycErrors({
      contact: '',
      aadhaar: '',
      pan: '',
      bankName: '',
      bankAccount: '',
      ifsc: ''
    });
    
    // Reset UID-related states
    setUidInput('');
    setUidValidation({ valid: null, error: '' });
    setUserProfile(null);
    setProfileLoading(false);
    setEmploymentStatus(null);
    
    setEditingEmployee(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentCompany?.companyId) {
      warning('No company selected', { title: 'Validation Error' });
      return;
    }

    // Validate required fields
    if (!formData.name || !formData.phone || !formData.designation) {
      warning('Please fill all required fields', { title: 'Validation Error' });
      return;
    }

    // Optional validation for UID-based addition (only when UID method is selected AND UID is provided)
    if (additionMethod === 'uid' && uidInput) {
      if (!uidValidation.valid) {
        warning('Please enter a valid User ID or switch to Manual Addition', { title: 'Validation Error' });
        return;
      }
      
      if (!userProfile) {
        warning('User profile not loaded. Please check the User ID or switch to Manual Addition.', { title: 'Validation Error' });
        return;
      }
    }
    
    // Manual addition requires email for basic contact
    if (additionMethod === 'manual' && !formData.email) {
      warning('Please provide email for manual employee addition', { title: 'Validation Error' });
      return;
    }

    // Check KYC errors
    const hasKycErrors = Object.values(kycErrors).some(error => error !== '');
    if (hasKycErrors) {
      warning('Please fix KYC validation errors', { title: 'Validation Error' });
      return;
    }

    try {
      const { department: _omitDept, ...formPayload } = formData;
      const salaryPayload = {
        department: '',
        ...buildSalaryPayload(formData.basicSalary, formData.bonus),
      };

      if (editingEmployee) {
        // Update existing employee
        console.log('🔄 Updating existing employee');
        
        const employeeData = {
          ...formPayload,
          ...salaryPayload,
          employeeId: editingEmployee.employeeId,
          companyId: currentCompany.companyId,
          
          // Documents
          documents: documents,
          
          // Keep existing addition method
          addedVia: editingEmployee.addedVia || 'manual'
        };

        const result = await updateEmployee(editingEmployee.id, employeeData);
        if (result.success) {
          success(`Employee ${formData.name} updated successfully!`, { 
            title: 'Employee Updated',
            details: `Employee ID: ${editingEmployee.employeeId || 'N/A'} • ${formData.designation || 'No designation'}`,
            duration: 6000 
          });
          resetForm();
          setActiveTab('list');
        } else {
          notifyError('Failed to update employee: ' + result.error, { title: 'Update Failed', duration: 7000 });
        }
        
      } else if (additionMethod === 'uid' && uidInput && userProfile) {
        // UID-based employee addition (when UID is provided)
        console.log('🔄 Adding existing user as employee via UID');
        
        const employeeData = {
          ...formPayload,
          ...salaryPayload,
          role: formData.role || 'employee',
          // Additional employee details
          joiningDate: formData.joiningDate || new Date(),
          employmentType: formData.employmentType || 'full_time'
        };
        
        const result = await UserProfileManager.addExistingUserAsEmployee(
          uidInput,
          currentCompany.companyId,
          employeeData
        );
        
        if (result.success) {
          // Refresh employee list to show the newly added employee
          if (loadCompanyEmployees) {
            await loadCompanyEmployees(currentCompany.companyId);
          }
          
          // Refresh user companies list in case it needs updating
          const currentUserId = auth?.currentUser?.uid;
          if (loadUserCompanies && currentUserId) {
            // Use setTimeout to ensure employee is fully added first
            setTimeout(() => {
              loadUserCompanies(currentUserId);
            }, 500);
          }
          
          success(`User successfully added as employee!`, { 
            title: 'Employee Added',
            details: `Employee ID: ${result.employeeId} • ${userProfile.displayName} • ${userProfile.email}`,
            duration: 6000 
          });
          resetForm();
          setActiveTab('list');
        } else {
          notifyError('Failed to add user as employee: ' + result.error, { title: 'Add Failed', duration: 7000 });
        }
        
      } else {
        // Manual employee addition (default for all other cases)
        console.log('🔄 Creating manual employee record');
        
        const employeeData = {
          ...formPayload,
          ...salaryPayload,
          role: formData.role || 'employee',
          joiningDate: formData.joiningDate || new Date(),
          employmentType: formData.employmentType || 'full_time',
          addedVia: 'manual',
          status: 'active',
          // No userId link for manual entries
          userId: null,
          inviteStatus: 'manual_entry'
        };
        
        const result = await createEmployee(employeeData);
        if (result.success) {
          success(`Employee added successfully!`, { 
            title: 'Employee Added',
            details: `Employee ID: ${result.employeeId} • ${employeeData.name} • Manual Entry`,
            duration: 6000 
          });
          resetForm();
          setActiveTab('list');
        } else {
          notifyError('Failed to add employee: ' + result.error, { title: 'Add Failed', duration: 7000 });
        }
      }
    } catch (err) {
      console.error('Error saving employee:', err);
      notifyError('Error saving employee: ' + err.message, { title: 'Save Failed', duration: 7000 });
    }
  };

  // Handle edit employee
  const handleEditEmployee = (employee) => {
    const { department: _omitDept, ...employeeRest } = employee;
    const bonusVal =
      employee.bonusAmount != null && employee.bonusAmount !== ''
        ? String(employee.bonusAmount)
        : (employee.bonus != null && employee.bonus !== ''
          ? String(employee.bonus)
          : (employee.salary_info?.bonus != null ? String(employee.salary_info.bonus) : ''));
    const basicVal =
      employee.basicSalary != null && employee.basicSalary !== ''
        ? String(employee.basicSalary)
        : (employee.basicSalaryAmount != null ? String(employee.basicSalaryAmount) : '');
    setFormData({
      ...employeeRest,
      joiningDate: employee.joiningDate || '',
      basicSalary: basicVal,
      bonus: bonusVal
    });
    setDocuments(employee.documents || {
      aadhaarCard: null,
      panCard: null,
      bankPassbook: null,
      photo: null,
      resume: null,
      otherDocuments: []
    });
    setEditingEmployee(employee);
    setActiveTab('edit');
  };

  // Handle delete employee
  const handleDeleteEmployee = async (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        const result = await deleteEmployee(employeeId);
        if (result.success) {
          success('Employee deleted successfully!', { 
            title: 'Employee Deleted',
            details: `${emp?.name || 'Employee'} • ${emp?.employeeId || 'No ID'}`,
            duration: 6000 
          });
        }
      } catch (err) {
        console.error('Error deleting employee:', err);
        notifyError('Error deleting employee: ' + err.message, { title: 'Delete Failed', duration: 7000 });
      }
    }
  };

  // Handle individual employee invite
  const handleEmployeeInvite = (employee) => {
    setSelectedEmployeeForInvite(employee);
    setShowInviteModal(true);
  };

  const isInactiveOrDeleted = (employee) => {
    const s = String(employee.status || 'active').toLowerCase();
    return s === 'inactive' || s === 'deleted';
  };

  // Dedupe by Firestore doc id (stored `id` in document data used to overwrite doc id and duplicate keys)
  const employeesDeduped = (() => {
    const byId = new Map();
    for (const e of employees || []) {
      if (e?.id) byId.set(e.id, e);
    }
    return [...byId.values()];
  })();

  // Filter employees
  const filteredEmployees = employeesDeduped.filter(employee => {
    const matchesSearch = !searchQuery || 
      employee.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.designation?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatusVisibility = showInactiveDeleted || !isInactiveOrDeleted(employee);
    const matchesRole = !filterRole || employee.role === filterRole;
    
    return matchesSearch && matchesStatusVisibility && matchesRole;
  });

  const legacySalaryCount = employeesDeduped.filter(employeeNeedsSalaryMigration).length;

  const handleMigrateLegacySalary = async () => {
    if (
      !window.confirm(
        `Normalize salary for ${legacySalaryCount} employee(s)? Legacy HRA/PF/conveyance amounts will be merged into annual basic; bonus is kept as-is.`
      )
    ) {
      return;
    }
    setMigratingSalary(true);
    try {
      const result = await migrateCompanyEmployeesSalary(employeesDeduped, updateEmployee);
      if (result.migrated > 0) {
        success(`Normalized salary for ${result.migrated} employee(s).`, { title: 'Salary migration' });
        if (loadCompanyEmployees && currentCompany?.companyId) {
          await loadCompanyEmployees(currentCompany.companyId);
        }
      }
      if (result.skipped > 0 && result.migrated === 0) {
        warning('No employees needed migration.', { title: 'Salary migration' });
      }
      if (result.errors?.length) {
        warning(`Some updates failed: ${result.errors.slice(0, 3).join('; ')}`, { title: 'Salary migration' });
      }
    } catch (err) {
      notifyError(err.message || 'Migration failed', { title: 'Salary migration' });
    } finally {
      setMigratingSalary(false);
    }
  };

  // Employee List Component
  const EmployeeList = () => (
    <div className="space-y-4">
      {legacySalaryCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p>
            <strong>{legacySalaryCount}</strong> employee(s) still use the old salary structure (HRA, PF, etc.).
            Run migration to use <strong>annual basic + bonus</strong> only.
          </p>
          <button
            type="button"
            disabled={migratingSalary}
            onClick={handleMigrateLegacySalary}
            className="shrink-0 rounded-lg bg-acctoo-600 px-4 py-2 text-sm font-medium text-white hover:bg-acctoo-700 disabled:opacity-50"
          >
            {migratingSalary ? 'Migrating…' : 'Normalize legacy salaries'}
          </button>
        </div>
      )}
      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Visibility</label>
            <AppSelect
              value={showInactiveDeleted ? 'all' : 'active'}
              onChange={(e) => setShowInactiveDeleted(e.target.value === 'all')}
              aria-label="Visibility"
              sheetTitle="Visibility"
              options={[
                { value: 'active', label: 'Active only' },
                { value: 'all', label: 'Include inactive & deleted' },
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
            <AppSelect
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              aria-label="Role"
              sheetTitle="Role"
              placeholder="All Roles"
              options={[
                { value: '', label: 'All Roles' },
                ...EMPLOYEE_ROLES.map(role => ({ value: role.value, label: role.label })),
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
          <div>
            <button
              type="button"
              onClick={() => setActiveTab('add')}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center font-medium shadow-sm"
            >
              <Plus size={16} className="mr-2 shrink-0" />
              Add Employee
            </button>
          </div>
        </div>
      </div>



      {/* Employee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map(employee => (
          <div key={employee.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users size={20} className="text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">{employee.name}</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">ID:</span> {employee.employeeId}
                    </p>
                    {employee.globalEmployeeId && (
                      <p className="text-xs text-gray-400">
                        <span className="font-medium">Global ID:</span> {employee.globalEmployeeId}
                      </p>
                    )}
                    {employee.userId && (
                      <p className="text-xs text-gray-400 font-mono">
                        <span className="font-medium">UID:</span> {employee.userId}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => {
                    setSelectedEmployeeForCashFlow(employee);
                    setActiveTab('cash');
                  }}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-purple-600 hover:bg-purple-50 rounded-lg touch-manipulation"
                  title="View Cash Ledger"
                  aria-label="View Cash Ledger"
                >
                  <Wallet size={18} />
                </button>
                <button
                  onClick={() => handleEditEmployee(employee)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg touch-manipulation"
                  title="Edit Employee"
                  aria-label="Edit Employee"
                >
                  <Edit3 size={18} />
                </button>
                <button
                  onClick={() => handleDeleteEmployee(employee.id)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg touch-manipulation"
                  title="Delete Employee"
                  aria-label="Delete Employee"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <Building size={14} className="mr-2" />
                {employee.designation || '—'}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <UserCheck size={14} className="mr-2" />
                {EMPLOYEE_ROLES.find(r => r.value === employee.role)?.label || employee.role}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <IndianRupee size={14} className="mr-2" />
                CTC: ₹{(employee.ctc || 0).toLocaleString()}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Wallet size={14} className="mr-2" />
                Cash: ₹{(employee.cashBalance?.cash || 0).toLocaleString()} | UPI: ₹{(employee.cashBalance?.upi || 0).toLocaleString()}
              </div>
              {/* Email removed from card to simplify UID-first flow */}
              <div className="flex items-center text-sm text-gray-600">
                <Phone size={14} className="mr-2" />
                {employee.phone || employee.contact || 'No phone'}
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  employee.status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {employee.status || 'active'}
                </span>
                
                {/* Invite Status */}
                {employee.inviteStatus && (
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    employee.inviteStatus === 'invited' 
                      ? 'bg-yellow-100 text-yellow-700'
                      : employee.inviteStatus === 'joined'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {employee.inviteStatus}
                  </span>
                )}
              </div>
              
              {/* Invite functionality removed - UID-only flow */}
            </div>
          </div>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-12 px-4">
          <div className="max-w-md mx-auto text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-4" strokeWidth={1.25} />
            <p className="text-gray-700 font-medium mb-1">No employees to show</p>
            <p className="text-sm text-gray-500">
              Adjust search or filters, or add someone using <span className="font-medium text-gray-700">Add Employee</span> in the bar above.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading employees...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Employee Management</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage employee information and KYC details</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Mobile tab select */}
      <div className="sm:hidden mb-4">
        <label className="block text-xs font-medium text-gray-500 mb-1">Section</label>
        <AppSelect
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          aria-label="Section"
          sheetTitle="Section"
          options={[
            { value: 'list', label: 'Employee List' },
            { value: 'add', label: 'Add Employee' },
            ...(editingEmployee ? [{ value: 'edit', label: 'Edit Employee' }] : []),
            ...(selectedEmployeeForCashFlow ? [{ value: 'cash', label: 'Cash Transactions' }] : []),
          ]}
          triggerClassName="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg text-base bg-white text-left flex items-center justify-between"
        />
      </div>

      {/* Navigation Tabs */}
      <div className="hidden sm:block mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-1">
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={16} className="inline mr-2" />
              Employee List
            </button>
            {editingEmployee && (
              <button
                onClick={() => setActiveTab('edit')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'edit'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Edit3 size={16} className="inline mr-2" />
                Edit Employee
              </button>
            )}
            {selectedEmployeeForCashFlow && (
              <button
                onClick={() => setActiveTab('cash')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'cash'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Wallet size={16} className="inline mr-2" />
                Cash Transactions
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'list' && <EmployeeList />}

      {activeTab === 'cash' && selectedEmployeeForCashFlow && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                Cash Flow - {selectedEmployeeForCashFlow.name}
              </h2>
              <p className="text-sm text-gray-600">
                Employee ID: {selectedEmployeeForCashFlow.employeeId}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedEmployeeForCashFlow(null);
                setActiveTab('list');
              }}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 shrink-0 touch-manipulation"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <EmployeeCashFlow 
            employeeId={selectedEmployeeForCashFlow.id}
            employeeName={selectedEmployeeForCashFlow.name}
          />
        </div>
      )}

      {(activeTab === 'add' || activeTab === 'edit') && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {activeTab === 'edit' ? 'Edit Employee' : 'Add New Employee'}
            </h2>
            <button
              onClick={() => {
                resetForm();
                setActiveTab('list');
              }}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 shrink-0 touch-manipulation"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              
              {/* Employee Addition Methods */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-900 mb-3">Employee Addition Options</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    additionMethod === 'uid' 
                      ? 'border-green-500 bg-green-100 text-green-900' 
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}>
                    <input
                      type="radio"
                      name="additionMethod"
                      value="uid"
                      checked={additionMethod === 'uid'}
                      onChange={(e) => handleAdditionMethodChange(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="font-medium">🔗 UID-based Addition</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Add existing ACCTOO user directly using their User ID
                      </div>
                    </div>
                  </label>
                  
                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    additionMethod === 'manual' 
                      ? 'border-blue-500 bg-blue-100 text-blue-900' 
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}>
                    <input
                      type="radio"
                      name="additionMethod"
                      value="manual"
                      checked={additionMethod === 'manual'}
                      onChange={(e) => handleAdditionMethodChange(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="font-medium">👤 Manual Addition</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Add employee details manually (for labor/non-tech workers)
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* UID Input Section - Optional for linking existing ACCTOO users */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  🔗 Link to Existing ACCTOO User (Optional)
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  If this employee already has an ACCTOO account, enter their User ID to link the accounts.
                </p>
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-sm font-medium text-green-900 mb-3">
                    🔍 Enter User ID (from Settings page)
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        User ID (UID) - Optional
                      </label>
                      <input
                        type="text"
                        value={uidInput}
                        onChange={handleUidInputChange}
                        placeholder="Enter the User ID from employee's Settings page"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          uidValidation.valid === null 
                            ? 'border-gray-300 focus:ring-blue-500' 
                            : uidValidation.valid 
                              ? 'border-green-500 focus:ring-green-500' 
                              : 'border-red-500 focus:ring-red-500'
                        }`}
                      />
                      
                      {/* UID Validation Status */}
                      {uidValidation.error && (
                        <p className="text-sm text-red-600 mt-1">❌ {uidValidation.error}</p>
                      )}
                      
                      {profileLoading && (
                        <p className="text-sm text-blue-600 mt-1">🔍 Loading user profile...</p>
                      )}
                    </div>
                    
                    {/* User Profile Preview */}
                    {userProfile && (
                      <div className="bg-white p-4 rounded-lg border border-green-300">
                        <h5 className="font-medium text-green-900 mb-2">✅ User Found!</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Name:</span>
                            <span className="ml-2 font-medium">{userProfile.displayName || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Email:</span>
                            <span className="ml-2 font-medium">{userProfile.email}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Phone:</span>
                            <span className="ml-2 font-medium">{userProfile.phoneNumber || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Verified:</span>
                            <span className={`ml-2 font-medium ${userProfile.emailVerified ? 'text-green-600' : 'text-yellow-600'}`}>
                              {userProfile.emailVerified ? '✅ Yes' : '⚠️ No'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Employment Status */}
                        {employmentStatus && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="text-sm">
                              <span className="text-gray-600">Current Employment:</span>
                              <span className="ml-2 font-medium">
                                {employmentStatus.isEmployed 
                                  ? `${employmentStatus.totalCompanies} company(ies)` 
                                  : 'Not employed anywhere'
                                }
                              </span>
                            </div>
                            {employmentStatus.companies.length > 0 && (
                              <div className="mt-2">
                                <span className="text-xs text-gray-500">
                                  Note: User is already employed in other companies. Adding to this company will create additional employment.
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Joining Date
                  </label>
                  <input
                    type="date"
                    name="joiningDate"
                    value={formData.joiningDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Role *
                  </label>
                  <AppSelect
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    aria-label="Employee Role"
                    sheetTitle="Employee Role"
                    options={EMPLOYEE_ROLES.map(role => ({ value: role.value, label: role.label }))}
                    triggerClassName={APP_SELECT_FILTER_TRIGGER}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <AppSelect
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    aria-label="Status"
                    sheetTitle="Status"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                      { value: 'suspended', label: 'Suspended' },
                    ]}
                    triggerClassName={APP_SELECT_FILTER_TRIGGER}
                  />
                </div>
              </div>
            </div>

            {/* KYC Information - MIGRATED FROM EXPENSES */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number
                  </label>
                  <input
                    type="text"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      kycErrors.contact ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {kycErrors.contact && (
                    <p className="text-red-500 text-xs mt-1">{kycErrors.contact}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aadhaar Number
                  </label>
                  <input
                    type="text"
                    name="aadhaar"
                    value={formData.aadhaar}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      kycErrors.aadhaar ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {kycErrors.aadhaar && (
                    <p className="text-red-500 text-xs mt-1">{kycErrors.aadhaar}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PAN Number
                  </label>
                  <input
                    type="text"
                    name="pan"
                    value={formData.pan}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      kycErrors.pan ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {kycErrors.pan && (
                    <p className="text-red-500 text-xs mt-1">{kycErrors.pan}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    name="bankAccount"
                    value={formData.bankAccount}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      kycErrors.bankAccount ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {kycErrors.bankAccount && (
                    <p className="text-red-500 text-xs mt-1">{kycErrors.bankAccount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      kycErrors.ifsc ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {kycErrors.ifsc && (
                    <p className="text-red-500 text-xs mt-1">{kycErrors.ifsc}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Document Upload - MIGRATED FROM EXPENSES */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Upload</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'aadhaarCard', label: 'Aadhaar Card', icon: CreditCard },
                  { key: 'panCard', label: 'PAN Card', icon: CreditCard },
                  { key: 'bankPassbook', label: 'Bank Passbook', icon: Briefcase },
                  { key: 'photo', label: 'Photo', icon: Camera },
                  { key: 'resume', label: 'Resume', icon: FileText }
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} className="border border-gray-200 rounded-lg p-4 text-center">
                    <Icon size={24} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleDocumentUpload(key, e.target.files[0])}
                      className="hidden"
                      id={`doc-${key}`}
                    />
                    <label
                      htmlFor={`doc-${key}`}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 rounded text-xs bg-white hover:bg-gray-50 cursor-pointer"
                    >
                      <Upload size={12} className="mr-1" />
                      Upload
                    </label>
                    {documents[key] && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Salary (annual) */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-acctoo-900 mb-4">Salary (annual)</h3>
              <div className="max-w-2xl bg-acctoo-50 border border-acctoo-100 rounded-lg p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-acctoo-800 mb-2">
                      Basic salary (annual) *
                    </label>
                    <input
                      type="number"
                      name="basicSalary"
                      min="0"
                      step="any"
                      value={formData.basicSalary}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-acctoo-200 rounded-md focus:outline-none focus:ring-2 focus:ring-acctoo-500"
                      placeholder="e.g. 600000"
                    />
                    <p className="text-xs text-acctoo-600 mt-1">
                      Monthly: ₹{((parseFloat(formData.basicSalary) || 0) / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-acctoo-800 mb-2">
                      Bonus (annual)
                    </label>
                    <input
                      type="number"
                      name="bonus"
                      min="0"
                      step="any"
                      value={formData.bonus}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-acctoo-200 rounded-md focus:outline-none focus:ring-2 focus:ring-acctoo-500"
                      placeholder="0"
                    />
                    <p className="text-xs text-acctoo-600 mt-1">
                      Monthly: ₹{((parseFloat(formData.bonus) || 0) / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                </div>
                <div className="bg-white border border-acctoo-200 rounded-lg p-4 flex flex-col sm:flex-row sm:justify-between gap-2">
                  <p className="text-base font-semibold text-acctoo-800">
                    Annual CTC: ₹{calculateCTC().toLocaleString()}
                  </p>
                  <p className="text-base font-semibold text-acctoo-700">
                    Monthly: ₹{(calculateCTC() / 12).toFixed(0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setActiveTab('list');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-acctoo-600 text-white rounded-md hover:bg-acctoo-700 transition-colors flex items-center"
                >
                  <Save size={16} className="mr-2" />
                  {activeTab === 'edit' ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Employee Invite Modal */}
      <EmployeeInviteModal 
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setSelectedEmployeeForInvite(null);
        }}
        currentCompany={currentCompany}
        selectedEmployee={selectedEmployeeForInvite}
        onInviteSent={() => {
          console.log('Invite sent from Employee Management');
          setShowInviteModal(false);
          setSelectedEmployeeForInvite(null);
        }}
      />
    </div>
  );
};

export default EmployeeManagement;